/**
 * 平板上的 App（我的 › 学生设备 › 安卓平板，与 iOS Me/DeviceAppsView.swift 一致，docs/API.md 2.3）：
 * 安卓学生端上报的已安装 App，可以请求卸载（平板上弹系统确认框，要在平板上点「确定」）、撤回请求、让平板重新上报。
 * 打开时读取一次；实时通道 device_apps_changed 是这台平板时重新读取。
 */
import { ChevronDown, ChevronUp, RefreshCw, Search, TabletSmartphone, TriangleAlert } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ApiError,
  appSubtitle,
  cancelUninstall,
  confirm,
  fetchDeviceApps,
  filterApps,
  isUninstallPending,
  onRealtime,
  refreshDeviceApps,
  refreshToastText,
  replaceApp,
  reportedText,
  requestUninstall,
  run,
  splitApps,
  toast,
  uninstallStatusText,
  uninstallToastText,
  type Device,
  type DeviceApp,
  type DeviceAppsResponse,
} from "../../core";
import { AppRow, AppSearchField } from "../../components/DeviceApps";
import { Button, EmptyHint, RowDivider, SectionLabel, Sheet, SheetBody, SheetHeader, Spinner } from "../../ui";

export function DeviceAppsSheet({ device, onClose }: { device: Device | null; onClose: () => void }) {
  return (
    <Sheet open={!!device} onClose={onClose} width={560} label="平板上的 App">
      {device && <DeviceAppsBody key={device.id} device={device} onClose={onClose} />}
    </Sheet>
  );
}

function DeviceAppsBody({ device, onClose }: { device: Device; onClose: () => void }) {
  const [data, setData] = useState<DeviceAppsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [showSystem, setShowSystem] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  /** 正在请求卸载 / 撤回的包名 */
  const [busyPkg, setBusyPkg] = useState<string | null>(null);
  const loadSeq = useRef(0);
  const reloadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    const my = ++loadSeq.current;
    try {
      const r = await fetchDeviceApps(device.id);
      if (my !== loadSeq.current) return;
      setData(r);
      setError(null);
    } catch (e) {
      if (my !== loadSeq.current || (e instanceof ApiError && e.isUnauthorized)) return;
      // 已经有列表时（实时通道触发的重新读取）失败不打断显示
      console.warn("[device-apps] 读取失败", e);
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [device.id]);

  useEffect(() => {
    void load();
    const off = onRealtime("device_apps_changed", (m) => {
      if (m.deviceId === device.id) void load();
    });
    return () => {
      off();
      if (reloadTimer.current) clearTimeout(reloadTimer.current);
    };
  }, [device.id, load]);

  async function refresh() {
    setRefreshing(true);
    const r = await run(() => refreshDeviceApps(device.id));
    setRefreshing(false);
    if (!r) return;
    toast(refreshToastText(r.online));
    // 平板在线时一般几秒内就上报完（也会收到 device_apps_changed），这里再兜底读一次
    if (reloadTimer.current) clearTimeout(reloadTimer.current);
    reloadTimer.current = setTimeout(() => void load(), 3000);
  }

  function updateApp(app: DeviceApp) {
    setData((d) => (d ? { ...d, apps: replaceApp(d.apps, app) } : d));
  }

  async function uninstall(a: DeviceApp) {
    const ok = await confirm({
      title: `卸载「${a.label}」？`,
      message: "平板上会弹出卸载确认，需要在平板上点「确定」。平板不在线时，下次打开学生端时弹出。",
      confirmText: "请求卸载",
      danger: true,
    });
    if (!ok) return;
    setBusyPkg(a.package);
    const r = await run(() => requestUninstall(device.id, a.package));
    setBusyPkg(null);
    if (!r) return;
    updateApp(r.app);
    toast(uninstallToastText(r.online));
  }

  async function withdraw(a: DeviceApp) {
    setBusyPkg(a.package);
    const r = await run(() => cancelUninstall(device.id, a.package), "已撤回");
    setBusyPkg(null);
    if (r) updateApp(r.app);
  }

  const groups = useMemo(() => splitApps(filterApps(data?.apps ?? [], query)), [data, query]);
  const name = data?.device.name ?? device.name;
  const reported = reportedText(data?.appsReportedAt) ?? (data ? "还没有上报" : "正在读取…");
  // 搜索时系统应用直接展开
  const systemExpanded = showSystem || query.trim() !== "";

  const row = (a: DeviceApp, i: number) => {
    const status = uninstallStatusText(a);
    const pending = isUninstallPending(a);
    const busy = busyPkg === a.package;
    return (
      <div key={a.package}>
        {i > 0 && <RowDivider />}
        <div className="dapps-row">
          <AppRow
            app={a}
            subtitle={appSubtitle(a)}
            trailing={
              !a.system && (
                <span className="dapps-trail">
                  {status && <span className={`dapps-status is-${a.uninstall?.status ?? ""}`}>{status}</span>}
                  {pending ? (
                    <button type="button" className="dapps-btn" disabled={busy} onClick={() => void withdraw(a)}>
                      {busy ? <Spinner size={14} /> : "撤回"}
                    </button>
                  ) : (
                    <button type="button" className="dapps-btn is-danger" disabled={busy} onClick={() => void uninstall(a)}>
                      {busy ? <Spinner size={14} /> : "卸载"}
                    </button>
                  )}
                </span>
              )
            }
          />
        </div>
      </div>
    );
  };

  let list;
  if (!data) {
    list = error ? (
      <EmptyHint icon={TriangleAlert} text={error}>
        <Button variant="soft" height={40} onClick={() => void load()}>
          重试
        </Button>
      </EmptyHint>
    ) : (
      <div className="dapps-loading">
        <Spinner size={24} />
      </div>
    );
  } else if (data.apps.length === 0) {
    list = <EmptyHint icon={TabletSmartphone} text="还没有上报，平板打开学生端后会自动上报" />;
  } else if (groups.removable.length === 0 && groups.system.length === 0) {
    list = <EmptyHint icon={Search} text={`没有找到「${query.trim()}」`} />;
  } else {
    list = (
      <>
        <div className="dapps-section">
          <SectionLabel>可以卸载（{groups.removable.length}）</SectionLabel>
          {groups.removable.length > 0 ? <div className="dapps-card">{groups.removable.map(row)}</div> : <p className="me-note">没有可以卸载的 App</p>}
        </div>
        {groups.system.length > 0 && (
          <div className="dapps-section">
            <button type="button" className="dapps-toggle" aria-expanded={systemExpanded} onClick={() => setShowSystem(!showSystem)}>
              系统应用（{groups.system.length}）
              {systemExpanded ? <ChevronUp size={14} strokeWidth={2.6} /> : <ChevronDown size={14} strokeWidth={2.6} />}
            </button>
            {systemExpanded && <div className="dapps-card">{groups.system.map(row)}</div>}
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <SheetHeader title={`「${name}」上的 App`} onClose={onClose} />
      <SheetBody gap={12}>
        <p className="me-note me-note--lg">平板打开学生端时自动上报。卸载要在平板上点「确定」才会执行。</p>
        <div className="dapps-report">
          <span>{reported}</span>
          <Button variant="soft" height={32} className="dapps-refresh" icon={<RefreshCw size={14} strokeWidth={2.4} />} busy={refreshing} onClick={() => void refresh()}>
            重新上报
          </Button>
        </div>
        {data && data.apps.length > 0 && <AppSearchField value={query} onChange={setQuery} />}
        {data && error && <p className="me-error">{error}</p>}
        {list}
      </SheetBody>
    </>
  );
}
